// An owner's ways in (UC-28): listing them, adding Google or a password, and
// removing one. Each call turns the server's answer into what Sign-in
// methods (D20) shows, so the screen holds no error codes.

import { api, apiBase } from '../api/client';
import type { components } from '../api/types';
import type { Language } from '../i18n/messages';

export type SignInMethod = components['schemas']['SignInMethodBody'];

/** The ways in, oldest first, or null when the server could not be reached. */
export async function loadMethods(): Promise<SignInMethod[] | null> {
	try {
		const { data } = await api.GET('/api/owner/sign-in-methods');
		return data ? (data.methods ?? []) : null;
	} catch {
		return null;
	}
}

export type AddPasswordResult =
	| { kind: 'sent'; email: string }
	| { kind: 'weak' }
	| { kind: 'badEmail' }
	| { kind: 'emailTaken' }
	| { kind: 'failed'; message: string };

/** Mails the link that adds the password; nothing is added until it is opened. */
export async function addPassword(
	email: string,
	password: string,
	language: Language,
): Promise<AddPasswordResult> {
	const address = email.trim();
	try {
		const { error, response } = await api.POST('/api/owner/sign-in-methods/password', {
			body: { email: address, password, language },
		});
		if (response.ok) return { kind: 'sent', email: address };
		switch (error?.code) {
			case 'PASSWORD_WEAK':
				return { kind: 'weak' };
			case 'IDENTITY_EMAIL_TAKEN':
				return { kind: 'emailTaken' };
			case 'VALIDATION_ERROR':
				return { kind: 'badEmail' };
			default:
				return { kind: 'failed', message: error?.message ?? '' };
		}
	} catch {
		return { kind: 'failed', message: '' };
	}
}

export type RemoveResult =
	{ kind: 'removed' } | { kind: 'last' } | { kind: 'failed'; message: string };

export async function removeMethod(id: string): Promise<RemoveResult> {
	try {
		const { error, response } = await api.DELETE('/api/owner/sign-in-methods/{identityId}', {
			params: { path: { identityId: id } },
		});
		if (response.ok) return { kind: 'removed' };
		if (error?.code === 'LAST_SIGN_IN_METHOD') return { kind: 'last' };
		return { kind: 'failed', message: error?.message ?? '' };
	} catch {
		return { kind: 'failed', message: '' };
	}
}

/**
 * Where Add Google goes. A full-page redirect, as signing in is: the browser
 * has to reach Google itself. It comes back to Sign-in methods with ?link=.
 */
export function googleLinkUrl(language: Language): string {
	return `${apiBase}/auth/google/link?lang=${encodeURIComponent(language)}`;
}

/** The outcomes ?link= can carry back from Google. */
export type LinkOutcome = 'added' | 'in-use' | 'email-taken' | 'unfinished';

export function linkOutcome(search: string): LinkOutcome | null {
	const value = new URLSearchParams(search).get('link');
	return value === 'added' ||
		value === 'in-use' ||
		value === 'email-taken' ||
		value === 'unfinished'
		? value
		: null;
}
